import axios, { type AxiosRequestConfig } from 'axios';
import axiosRetry from 'axios-retry';

import { ApiRequest } from './request';
import { ApiException } from './exception';

export interface ApiClientErrorHandler {
  onAuthenticationError?: (exception: ApiException) => void;
  onPermissionError?: (exception: ApiException) => void;
  onMaintenanceError?: (exception: ApiException) => void;
}

const client = axios.create();

axiosRetry(client, {
  retries: 5,
  retryDelay: (retryCount) => {
    return retryCount * 1000;
  },
  retryCondition: error => error.code !== 'ECONNABORTED' && !error.response
});

// Allow injecting error handlers from the Nuxt plugin layer
let errorHandler: ApiClientErrorHandler = {};

export function setApiClientErrorHandler(handler: ApiClientErrorHandler) {
  errorHandler = handler;
}

export class ApiClient {
  static async request(
    request: ApiRequest,
    {
      serverRequest,
    }: {
      serverRequest?: Request;
    } = {}
  ): Promise<any> {
    try {
      const url = normalizeRequestUrl((request as any)?.url);

      if (!url || url === '[object Object]') {
        throw new Error(`Invalid API request URL: ${String((request as any)?.url)}`);
      }

      const config: AxiosRequestConfig = {
        method: request.method,
        url,
        headers: request.headers,
        params: request.query,
        data: request.data,
        withCredentials: request.authenticated !== false,
        responseType: request.responseType,
        signal: request.signal,
        validateStatus: (status: number) => {
          return (status >= 200 && status < 300) || status === 400 || status === 422
        },
      };

      // Apply upload progress callback if provided
      if (request.onUploadProgress) {
        config.onUploadProgress = request.onUploadProgress;
      }

      // Attach SSR cookies
      if (import.meta.server && serverRequest?.headers) {
        const cookie = serverRequest.headers.get('cookie');
        if (cookie) {
          config.headers = {
            ...config.headers,
            cookie,
          };
        }
      }

      // Support test API token from environment
      if (process.env.TEST_API_TOKEN) {
        config.headers = {
          ...config.headers,
          'Authorization': `Token token="${process.env.TEST_API_TOKEN}"`,
        };
      }

      const response = await client.request(config);
      const responseData = response.data;

      return responseData
    } catch (error: any) {
      const exception = new ApiException(error);

      switch (exception.code) {
        // Authentication Error
        case 401:
          errorHandler.onAuthenticationError?.(exception);
          throw exception;

        // Permission Error
        case 403:
          errorHandler.onPermissionError?.(exception);
          throw exception;

        // Maintenance
        case 503:
          errorHandler.onMaintenanceError?.(exception);
          throw exception;

        default:
          throw exception;
      }
    }
  }
}

function normalizeRequestUrl(url: unknown): string {
  if (typeof url === 'string') {
    return url;
  }

  if (url && typeof url === 'object') {
    const source = url as Record<string, unknown>;
    const value =
      source.url ??
      source.path ??
      source.href ??
      source.value ??
      source.endpoint;

    if (typeof value === 'string') {
      return value;
    }
  }

  return String(url ?? '');
}
